import * as React from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { Loader, Mic, Send, StopCircle } from "lucide-react";
import CompassLogo from "./CompassLogo";
import MessageLoading from "./MessageLoading";
import ScrollToBottomButton from "./ScrollToBottomButton";
import type { ICompassMessage } from "@/types/compass.types";
import { shouldShowCompassThinking } from "./CompassDialog.utils";

const COMPASS_INTRO_TEXT = `**Hi! I'm Compass,**

I am your legal project manager.

1. Please tell me the context of the situation.
2. And the tasks you want done or outcome(s) you are looking to achieve.

I'll create a plan and instruct the other AI's to do the work for you.

Or, if you are not sure where to start, just let me know.`;

const MOTION_TRANSITION = { duration: 0.3, ease: "easeOut" } as const;
const LAZY_FALLBACK = <div className="h-10 w-full animate-pulse bg-neutral-100 rounded" />;

export const CompassEmptyState: React.FC = () => (
  <div className="bg-neutral-700 text-neutral-200 px-4 py-3 rounded-lg text-sm flex flex-col gap-2 w-full">
    <div className="flex flex-col items-center gap-4">
      <CompassLogo containerClassName="size-24" size="lg" />
      <div className="flex-1 prose prose-sm prose-li:marker:text-neutral-200 max-w-none">
        <ReactMarkdown
          components={{
            a: ({ node: _node, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
          }}
        >
          {COMPASS_INTRO_TEXT}
        </ReactMarkdown>
      </div>
    </div>
  </div>
);

const LazyCompassMessage = React.lazy(() => import("./CompassMessage"));
const LazyActionItems = React.lazy(() => import("./ActionItems"));

const CompassMessageItem: React.FC<{ message: ICompassMessage; messages: ICompassMessage[] }> = ({
  message,
  messages,
}) => {
  if (!(message.isAction ?? false)) {
    return (
      <motion.div
        initial={{ opacity: 0, x: message.isSender ? 20 : -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={MOTION_TRANSITION}
        className="w-full"
      >
        <React.Suspense fallback={<div className="h-10" />}>
          <LazyCompassMessage message={message} type={message.isSender ? "user" : "assistant"} />
        </React.Suspense>
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={MOTION_TRANSITION}
      className="w-full"
    >
      <React.Suspense fallback={<div className="h-20" />}>
        <LazyActionItems message={message} messages={messages} />
      </React.Suspense>
    </motion.div>
  );
};

const CompassActionButton: React.FC<{
  isRecording: boolean;
  isMicroLoading: boolean;
  isConverting: boolean;
  chatMessage: string;
  isAssistantTyping: boolean;
  onMicClick: () => void;
  onSend: () => void;
}> = ({
  isRecording,
  isMicroLoading,
  isConverting,
  chatMessage,
  isAssistantTyping,
  onMicClick,
  onSend,
}) => {
  if (isRecording || isMicroLoading) {
    return (
      <button
        type="button"
        className="size-10 p-0 bg-danger-200 hover:bg-danger-200/90 text-white rounded-md flex items-center justify-center"
        onClick={onMicClick}
        title="Stop recording"
      >
        <StopCircle size={20} />
      </button>
    );
  }
  if (chatMessage.trim() === "") {
    return (
      <button
        type="button"
        className="size-10 p-0 bg-compass-action hover:bg-compass-action/90 text-white rounded-md flex items-center justify-center"
        onClick={onMicClick}
        disabled={isMicroLoading || isConverting || isAssistantTyping}
        title="Voice message"
      >
        {isMicroLoading ? <Loader className="animate-spin" size={20} /> : <Mic size={20} />}
      </button>
    );
  }
  return (
    <button
      type="button"
      className="size-10 p-0 bg-compass-action hover:bg-compass-action/90 text-white rounded-md flex items-center justify-center"
      onClick={onSend}
      disabled={!chatMessage.trim() || isAssistantTyping}
      title="Send message"
    >
      <Send size={20} />
    </button>
  );
};

interface CompassMessagesAreaProps {
  messages: ICompassMessage[];
  isLoading: boolean;
  isAssistantTyping: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
}

export const CompassMessagesArea: React.FC<CompassMessagesAreaProps> = ({
  messages,
  isLoading,
  isAssistantTyping,
  scrollContainerRef,
  messagesEndRef,
}) => {
  const lastMessageRef = React.useRef<HTMLDivElement>(null);

  // Find the index of the last non-sender message (assistant/action)
  const lastAssistantIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg != null && !(msg.isSender ?? false)) return i;
    }
    return -1;
  })();

  return (
    <div className="flex-1 relative min-h-0 flex flex-col">
      <div
        className="flex-1 overflow-y-auto px-2 space-y-4"
        ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
      >
        {messages.length === 0 && !isLoading && !isAssistantTyping && <CompassEmptyState />}
        {isLoading && messages.length === 0 && (
          <div className="flex justify-center p-6 gap-2">
            <Loader className="animate-spin" size={20} />
            <span>Loading chat history...</span>
          </div>
        )}
        {messages.map((message, idx) => (
          <div key={idx} ref={idx === lastAssistantIdx ? lastMessageRef : undefined}>
            <CompassMessageItem message={message} messages={messages} />
          </div>
        ))}
        {shouldShowCompassThinking(isAssistantTyping, messages) && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={MOTION_TRANSITION}
            className="w-full"
          >
            <React.Suspense fallback={<div className="h-8" />}>
              <MessageLoading text="AI is thinking" />
            </React.Suspense>
          </motion.div>
        )}
        <div ref={messagesEndRef as React.RefObject<HTMLDivElement>} />
      </div>
      <ScrollToBottomButton
        targetRef={lastMessageRef}
        containerRef={scrollContainerRef}
        dependency={messages}
      />
    </div>
  );
};

interface CompassInputAreaProps {
  isRecording: boolean;
  isMicroLoading: boolean;
  isSpeaking: boolean;
  isConverting: boolean;
  chatMessage: string;
  isAssistantTyping: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  handleTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSendMessage: () => void;
  startDictation: () => void;
  documentReady?: boolean;
}

const Textarea = React.lazy(() =>
  import("./ui/textarea").then((module) => ({ default: module.Textarea }))
);
const SoundWave = React.lazy(() => import("./SoundWave"));

export const CompassInputArea: React.FC<CompassInputAreaProps> = (p) => (
  <div className="flex flex-col border-t">
    {p.documentReady === false && (
      <p className="px-4 pt-2 text-xs text-danger-200">
        Quillio is still linking this Word file. Send will be available in a moment.
      </p>
    )}
    <div className="flex flex-row items-end gap-2 p-4">
      <div className="relative flex-1 min-w-0">
        <div className="relative items-center pr-1.5 min-h-10 flex border rounded-md overflow-hidden animate-border-pulse focus-within:animate-none transition-shadow duration-300">
          <div className="relative flex-1 min-w-0">
            {(p.isRecording || p.isMicroLoading) && (
              <div className="absolute inset-0 z-0">
                <React.Suspense fallback={LAZY_FALLBACK}>
                  <SoundWave />
                </React.Suspense>
              </div>
            )}
            <React.Suspense fallback={LAZY_FALLBACK}>
              <Textarea
                ref={p.textareaRef as React.RefObject<HTMLTextAreaElement>}
                className="relative z-10 flex-1 w-full border-none focus:ring-0 focus-visible:ring-0 p-0 outline-none resize-none pl-2 bg-transparent text-sm min-h-5 shadow-none py-1.5 placeholder:text-neutral-600 break-words"
                placeholder={
                  p.isRecording || p.isMicroLoading
                    ? ""
                    : p.documentReady === false
                      ? "Linking this Word file…"
                      : "Type or Dictate your outcome."
                }
                value={p.chatMessage}
                onChange={p.handleTextareaChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (p.documentReady !== false) p.handleSendMessage();
                  }
                }}
                rows={1}
                disabled={p.isAssistantTyping || p.documentReady === false}
              />
            </React.Suspense>
          </div>
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <CompassActionButton
          isRecording={p.isRecording}
          isMicroLoading={p.isMicroLoading}
          isConverting={p.isConverting}
          chatMessage={p.chatMessage}
          isAssistantTyping={p.isAssistantTyping || p.documentReady === false}
          onMicClick={p.startDictation}
          onSend={p.handleSendMessage}
        />
      </div>
    </div>
  </div>
);
